// ============================================================================
// hooks/liveStreamManagment/useDeckMixMonitor.ts
//
// Écoute du bloc Mixeur du deck (2026-10-21) : une SORTIE DE SON à part, sur le
// modèle du brouillon de la fenêtre Mixeur et de l'aperçu de la config d'une
// source (useSlotAudioPreview). Allumée par le haut-parleur du titre du bloc.
//
// Pour chaque source du deck, une entrée `audio-deckmix-<slot>` est empruntée sur
// le MÊME flux que l'entrée réelle (pas de second getUserMedia), réglée comme la
// source (niveaux G/D, volume, mute, effets) et branchée sur les seules enceintes
// de la régie. Jamais raccordée au bus : l'antenne n'en sait rien.
//
// Écoute allumée, elle REMPLACE l'écoute locale des entrées réelles de ces
// sources (registry.suppressMonitor) au lieu de s'y ajouter : sinon une source
// déjà audible par le son du flux final ou le Mix s'entendait deux fois.
// Écoute coupée, le bloc ne fait plus aucun son, et les entrées réelles
// retrouvent leur écoute habituelle (son du flux final, Switch audio).
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import type { AudioSourceRegistry } from '../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { AUDIO_EFFECTS } from '../../components/block/liveStreamManagment/audioEffects/registry';

export interface DeckMixMonitorSource {
  sourceId     : string;
  stereo       : { left: number; right: number };
  /** Gain mono historique du slot (SourceAudioConfig.volume). */
  volume       : number;
  muted        : boolean;
  effects      : Record<string, boolean>;
  effectConfigs: Record<string, unknown>;
}

const monitorId = (sourceId: string) => `audio-deckmix-${sourceId}`;

export function useDeckMixMonitor(
  registry: AudioSourceRegistry | undefined,
  enabled : boolean,
  sources : DeckMixMonitorSource[],
): void {
  const handlesRef = useRef(new Map<string, { release: () => void; stream: MediaStream }>());
  const idsKey = sources.map(s => s.sourceId).join('|');

  // Le registre n'est pas réactif : l'entrée réelle peut naître ou être recréée
  // (changement de source audio) — on suit les générations.
  const [generations, setGenerations] = useState('');
  useEffect(() => {
    if (!enabled || !registry) return;
    const timer = setInterval(() => {
      const g = idsKey.split('|').map(id => (registry.has(`audio-${id}`) ? registry.getGeneration(`audio-${id}`) : -1)).join(',');
      setGenerations(prev => (prev === g ? prev : g));
    }, 400);
    return () => clearInterval(timer);
  }, [enabled, registry, idsKey]);

  const [ready, setReady] = useState(0);

  // Emprunts : un par source voulue, rendus dès qu'une source quitte le deck,
  // que son flux change ou que l'écoute est coupée.
  useEffect(() => {
    if (!registry) return;
    const handles = handlesRef.current;
    const wanted  = new Set(enabled && idsKey ? idsKey.split('|') : []);
    let changed = false;

    handles.forEach((h, id) => {
      if (wanted.has(id) && registry.getStream(`audio-${id}`) === h.stream) return;
      h.release();
      handles.delete(id);
      changed = true;
    });

    wanted.forEach(id => {
      if (handles.has(id)) return;
      const stream = registry.getStream(`audio-${id}`);
      if (!stream) return;
      try {
        const handle = registry.borrowFromStream(monitorId(id), stream, 'stream', false);
        handles.set(id, { release: () => handle.release(), stream });
        registry.setMonitorGain(monitorId(id), 1);
        changed = true;
      } catch (err) {
        console.warn(`[useDeckMixMonitor] écoute de ${id} impossible :`, err);
      }
    });

    if (changed) setReady(n => n + 1);
  }, [registry, enabled, idsKey, generations]);

  // Pendant l'écoute du bloc, l'écoute locale des entrées RÉELLES des sources du deck
  // est coupée d'office (suppressMonitor) : elles ne s'entendent plus que par le bloc,
  // une seule fois, quoi que demandent le son du flux final, le Switch audio ou le Mix.
  // L'antenne n'est pas touchée (outputGain). Levée à l'arrêt de l'écoute.
  const suppressedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!registry) return;
    const wanted = new Set(enabled && idsKey ? idsKey.split('|').map(id => `audio-${id}`) : []);
    suppressedRef.current.forEach(regId => {
      if (wanted.has(regId)) return;
      registry.suppressMonitor(regId, false);
      suppressedRef.current.delete(regId);
    });
    wanted.forEach(regId => {
      if (suppressedRef.current.has(regId)) return;
      registry.suppressMonitor(regId, true);
      suppressedRef.current.add(regId);
    });
  }, [registry, enabled, idsKey]);

  // Tout rendre au démontage du deck.
  useEffect(() => () => {
    handlesRef.current.forEach(h => h.release());
    handlesRef.current.clear();
    suppressedRef.current.forEach(regId => registry?.suppressMonitor(regId, false));
    suppressedRef.current.clear();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Réglages : relus à chaque changement, sans relancer les emprunts.
  const settingsKey = JSON.stringify(sources);
  const sourcesRef  = useRef(sources);
  sourcesRef.current = sources;

  useEffect(() => {
    if (!registry || !enabled) return;
    for (const s of sourcesRef.current) {
      const id = monitorId(s.sourceId);
      if (!handlesRef.current.has(s.sourceId) || !registry.has(id)) continue;
      registry.setMuted(id, s.muted);
      for (const effect of AUDIO_EFFECTS) {
        effect.apply(registry, id, !!s.effects[effect.id], s.effectConfigs[effect.id] ?? effect.defaultConfig);
      }
      if (!s.muted) {
        registry.setGain(id, s.volume);
        registry.setGainLeft(id, s.stereo.left);
        registry.setGainRight(id, s.stereo.right);
      }
    }
  }, [registry, enabled, settingsKey, ready]);
}
